import React, { useState, useEffect, useRef } from "react"
import { useParams } from "react-router-dom"
import { proxyImage } from "../functions"

export default function ImageSlider({ images }) {
    const { id } = useParams()

    const initialIndex = images.findIndex(img => img.id === id)

    const [index, setIndex] = useState(initialIndex !== -1 ? initialIndex : 0)
    const [open, setOpen] = useState(true)
    const [showUI, setShowUI] = useState(true)
    const [loaded, setLoaded] = useState(false)
    const [prevIndex, setPrevIndex] = useState(null)
    const [direction, setDirection] = useState(1)
    const [animating, setAnimating] = useState(false)
    const [isAutoPlay, setIsAutoPlay] = useState(false)
    const autoPlayRef = useRef(null)

    const [scale, setScale] = useState(1)
    const [pos, setPos] = useState({ x: 0, y: 0 })

    const timerRef = useRef(null)
    const thumbsRef = useRef(null)
    const thumbsDragging = useRef(false)
    const thumbsStartX = useRef(0)
    const thumbsScrollLeft = useRef(0)
    const thumbsMoved = useRef(false)
    const fullscreenEntered = useRef(false)

    const lastTouch = useRef({
        distance: 0,
        center: { x: 0, y: 0 },
        startPos: { x: 0, y: 0 },
        scaleStart: 1
    })

    const dragging = useRef(false)
    const start = useRef({ x: 0, y: 0 })
    const startPos = useRef({ x: 0, y: 0 })

    const touch = useRef({
        distance: 0,
        scaleStart: 1
    })

    const swipe = useRef({
        startX: 0,
        startY: 0,
        active: false
    })

    const panTouch = useRef({
        active: false,
        startX: 0,
        startY: 0,
        startPos: { x: 0, y: 0 }
    })

    // autoplay
    const getCenter = (t1, t2) => ({
        x: (t1.clientX + t2.clientX) / 2,
        y: (t1.clientY + t2.clientY) / 2
    })

    const stopAutoPlay = () => {
        if (isAutoPlay) setIsAutoPlay(false)
    }

    const next = () => {
        userAction()
        stopAutoPlay()
        if (animating) return
        setLoaded(false)
        setDirection(1)
        setPrevIndex(index)
        setAnimating(true)
        setIndex(i => (i + 1) % images.length)
    }

    const prev = () => {
        userAction()
        stopAutoPlay()
        if (animating) return
        setLoaded(false)
        setDirection(-1)
        setPrevIndex(index)
        setAnimating(true)
        setIndex(i => (i - 1 + images.length) % images.length)
    }

    const close = () => {
        window.close()
        setOpen(false)
    }

    const clamp = (val, min, max) => Math.min(max, Math.max(min, val))

    const resetUI = () => {
        setShowUI(true)
        clearTimeout(timerRef.current)
        timerRef.current = setTimeout(() => setShowUI(false), 5000)
    }

    const setVH = () => {
        document.documentElement.style.setProperty(
            "--vh",
            `${window.innerHeight * 0.01}px`
        )
    }

    const getDistance = (t1, t2) => {
        const dx = t1.clientX - t2.clientX
        const dy = t1.clientY - t2.clientY
        return Math.hypot(dx, dy)
    }

    const onThumbsMouseDown = (e) => {
        thumbsDragging.current = true
        thumbsMoved.current = false
        thumbsStartX.current = e.pageX - thumbsRef.current.offsetLeft
        thumbsScrollLeft.current = thumbsRef.current.scrollLeft
    }

    const onThumbsMouseMove = (e) => {
        if (!thumbsDragging.current) return
        e.preventDefault()

        thumbsMoved.current = true

        const x = e.pageX - thumbsRef.current.offsetLeft
        const walk = x - thumbsStartX.current

        thumbsRef.current.scrollLeft = thumbsScrollLeft.current - walk
    }

    const onThumbsMouseUp = () => {
        thumbsDragging.current = false
    }

    const handleWheel = (e) => {
        userAction()
        stopAutoPlay()
        e.preventDefault()

        const rect = e.currentTarget.getBoundingClientRect()

        const cursorX = e.clientX - rect.left
        const cursorY = e.clientY - rect.top

        setScale(prev => {
            const next = clamp(prev + (e.deltaY < 0 ? 0.15 : -0.15), 1, 3)

            setPos(p => ({
                x: p.x - (cursorX - rect.width / 2) * (next - prev) / prev,
                y: p.y - (cursorY - rect.height / 2) * (next - prev) / prev
            }))

            return next
        })
    }

    const handleDoubleClick = () => {
        userAction()
        stopAutoPlay()
        setScale(prev => (prev === 1 ? 2.5 : 1))
        setPos({ x: 0, y: 0 })
    }

    const onMouseDown = (e) => {
        userAction()
        stopAutoPlay()
        if (scale === 1) return
        if (e.button !== 0) return

        e.preventDefault()

        dragging.current = true
        start.current = { x: e.clientX, y: e.clientY }
        startPos.current = pos
    }

    const onMouseMove = (e) => {
        if (!dragging.current) return

        if (e.buttons !== 1) {
            dragging.current = false
            return
        }

        const dx = e.clientX - start.current.x
        const dy = e.clientY - start.current.y

        setPos({
            x: startPos.current.x + dx,
            y: startPos.current.y + dy
        })
    }

    const onMouseUp = () => {
        dragging.current = false
    }

    const onTouchStart = (e) => {
        stopAutoPlay()
        if (thumbsRef.current?.contains(e.target)) return
        resetUI()

        // 2 пальца → zoom
        if (e.touches.length === 2) {
            const t1 = e.touches[0]
            const t2 = e.touches[1]

            lastTouch.current.distance = getDistance(t1, t2)
            lastTouch.current.center = getCenter(t1, t2)
            lastTouch.current.startPos = pos
            lastTouch.current.scaleStart = scale
            return
        }

        // 1 палец + zoom → PAN
        if (e.touches.length === 1 && scale > 1) {
            panTouch.current.active = true
            panTouch.current.startX = e.touches[0].clientX
            panTouch.current.startY = e.touches[0].clientY
            panTouch.current.startPos = pos
            return
        }

        // 1 палец + scale === 1 → SWIPE
        if (e.touches.length === 1 && scale === 1) {
            swipe.current.active = true
            swipe.current.startX = e.touches[0].clientX
            swipe.current.startY = e.touches[0].clientY
        }
    }

    const onTouchMove = (e) => {
        if (thumbsRef.current?.contains(e.target)) return
        e.preventDefault()

        if (e.touches.length === 2) {
            const t1 = e.touches[0]
            const t2 = e.touches[1]

            const newDistance = getDistance(t1, t2)
            const factor = newDistance / lastTouch.current.distance

            const newScale = clamp(scale * factor, 1, 3)

            const center = getCenter(t1, t2)

            const dx = center.x - lastTouch.current.center.x
            const dy = center.y - lastTouch.current.center.y

            setScale(newScale)
            setPos({
                x: lastTouch.current.startPos.x + dx,
                y: lastTouch.current.startPos.y + dy
            })

            return
        }

        // PAN (1 палец + zoom)
        if (e.touches.length === 1 && panTouch.current.active && scale > 1) {
            const dx = e.touches[0].clientX - panTouch.current.startX
            const dy = e.touches[0].clientY - panTouch.current.startY

            setPos({
                x: panTouch.current.startPos.x + dx,
                y: panTouch.current.startPos.y + dy
            })

            return
        }

        // SWIPE (1 палец + scale === 1)
        if (e.touches.length === 1 && swipe.current.active && scale === 1) {
            const dx = e.touches[0].clientX - swipe.current.startX
            const dy = e.touches[0].clientY - swipe.current.startY

            if (Math.abs(dy) > Math.abs(dx)) return

            if (Math.abs(dx) > 50) {
                swipe.current.active = false

                if (dx < 0) next()
                else prev()
            }
        }
    }

    const onTouchEnd = () => {
        touch.current.distance = 0
        lastTouch.current.distance = 0

        swipe.current.active = false
        panTouch.current.active = false
    }

    const enterFullscreen = () => {
        if (!fullscreenEntered.current) {
            fullscreenEntered.current = true
            if (!document.fullscreenElement) {
                document.documentElement.requestFullscreen().catch(() => {})
            }
        }
    }

    const userAction = () => {
        setIsAutoPlay(false)
        enterFullscreen()
    }


    // autoplay
    useEffect(() => {
        if (isAutoPlay && images.length > 1) {
            autoPlayRef.current = setInterval(() => {
                setLoaded(false)
                setDirection(1)
                setPrevIndex(prev => (prev === null ? index : prev))
                setAnimating(true)
                setIndex(i => (i + 1) % images.length)
            }, 6000)
        } else {
            clearInterval(autoPlayRef.current)
        }

        return () => clearInterval(autoPlayRef.current)
    }, [isAutoPlay, index, images.length])

    useEffect(() => {
        if (!animating) return

        const t = setTimeout(() => {
            setAnimating(false)
            setPrevIndex(null)
        }, 300)

        return () => clearTimeout(t)
    }, [animating])

    useEffect(() => {
        setVH()
        window.addEventListener("resize", setVH)

        return () => window.removeEventListener("resize", setVH)
    }, [])

    useEffect(() => {
        resetUI()
        return () => clearTimeout(timerRef.current)
    }, [])

    useEffect(() => {
        setScale(1)
        setPos({ x: 0, y: 0 })
    }, [index])

    useEffect(() => {
        const up = () => (dragging.current = false)

        window.addEventListener("mouseup", up)
        window.addEventListener("mouseleave", up)

        return () => {
            window.removeEventListener("mouseup", up)
            window.removeEventListener("mouseleave", up)
        }
    }, [])

    useEffect(() => {
        const el = thumbsRef.current
        if (!el) return

        const active = el.children[index]
        if (!active) return

        active.scrollIntoView({
            behavior: "smooth",
            inline: "center",
            block: "nearest"
        })
    }, [index])

    useEffect(() => {
        document.documentElement.style.setProperty(
            "--slide-dir",
            direction === 1 ? "40px" : "-40px"
        )
    }, [direction])

    useEffect(() => {
        const el = document.querySelector("#image-slider-root") || document.body
        const handler = (e) => {
            if (thumbsRef.current?.contains(e.target)) return
            onTouchMove(e)
        }
        el.addEventListener("touchmove", handler, { passive: false })
        return () => {
            el.removeEventListener("touchmove", handler)
        }
    }, [scale, pos])

    useEffect(() => {
        const timeout = setTimeout(() => {
            const active = thumbsRef.current.children[index]
            if (!active) return

            active.scrollIntoView({
                behavior: "smooth",
                inline: "center",
                block: "nearest"
            })
        }, 100)

        return () => clearTimeout(timeout)
    }, [index])

    if (!open) return null

    const controlStyle = {
        background: "rgba(0,0,0,0.35)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        border: "1px solid rgba(255,255,255,0.15)",
        borderRadius: 10
    }

    if (document.querySelector("#navbar") && document.querySelector("#footer")) {
        document.querySelector("#navbar").className = "d-none"
        document.querySelector("#footer").className = "d-none"
        document.querySelector("#main-field").style.paddingBottom = "0px"

        return (
            <div
                style={{
                    width: "100%",
                    height: "calc(var(--vh) * 100)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    background: "#000",
                    overflow: "hidden"
                }}
                onMouseMove={resetUI}
                onClick={resetUI}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
            >
                <div
                    style={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        overflow: "hidden"
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            width: "100%",
                            height: "100%",
                            transform: animating
                                ? `translateX(${direction === 1 ? "-100%" : "100%"})`
                                : "translateX(0)",
                            transition: animating ? "transform 0.3s ease" : "none"
                        }}
                    >
                        {/* PREV */}
                        {prevIndex !== null && (
                            <div
                                style={{
                                    minWidth: "100%",
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}
                            >
                                <img
                                    referrerPolicy="no-referrer"
                                    src={proxyImage(images[prevIndex].src)}
                                    alt={images[prevIndex]?.name || ""}
                                    style={{
                                        maxWidth: "100%",
                                        maxHeight: "100%",
                                        objectFit: "contain"
                                    }}
                                />
                            </div>
                        )}

                        {/* CURRENT */}
                        <div
                            style={{
                                minWidth: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative"
                            }}
                        >
                            {/* THUMB */}
                            <img
                                src={proxyImage(images[index].thumb)}
                                alt={images[index]?.name || ""}
                                style={{
                                    position: "absolute",
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                    filter: "blur(5px)",
                                    transform: "scale(1.00)",
                                    opacity: loaded || animating ? 0 : 1
                                }}
                            />

                            {/* FULL */}
                            <img
                                referrerPolicy="no-referrer"
                                key={images[index]?.id}
                                src={proxyImage(images[index].src)}
                                alt={images[index]?.name || ""}
                                onLoad={() => setLoaded(true)}
                                onError={() => setLoaded(true)}
                                onWheel={handleWheel}
                                onDoubleClick={handleDoubleClick}
                                onMouseDown={onMouseDown}
                                onMouseMove={onMouseMove}
                                onMouseUp={onMouseUp}
                                onMouseLeave={onMouseUp}
                                onTouchStart={onTouchStart}
                                onTouchMove={onTouchMove}
                                onTouchEnd={onTouchEnd}
                                draggable={false}
                                onDragStart={(e) => e.preventDefault()}
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                    objectFit: "contain",
                                    opacity: animating ? 0 : 1,
                                    transition: "opacity 0.15s linear",
                                    zIndex: "1",
                                    transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`
                                }}
                            />
                        </div>
                    </div>
                </div>

                <button
                    onClick={close}
                    style={{
                        position: "absolute",
                        top: "calc(10px + env(safe-area-inset-top))",
                        right: "calc(10px + env(safe-area-inset-right))",
                        fontSize: 20,
                        color: "white",
                        width: 40,
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(0,0,0,0.35)",
                        backdropFilter: "blur(10px)",
                        WebkitBackdropFilter: "blur(10px)",
                        border: "1px solid rgba(255,255,255,0.15)",
                        borderRadius: 10,
                        cursor: "pointer",
                        opacity: showUI ? 1 : 0,
                        transition: "0.3s"
                    }}
                >
                    <i className={"fa-solid fa-xmark"} />
                </button>

                {/* title + play/stop */}
                <div
                    style={{
                        position: "absolute",
                        top: "calc(10px + env(safe-area-inset-top))",
                        left: "50%",
                        transform: "translateX(-50%)",
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "6px 12px",
                        color: "white",
                        fontSize: 20,
                        maxWidth: "70%",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        ...controlStyle,
                        opacity: showUI ? 1 : 0,
                        transition: "0.3s"
                    }}
                >
                    <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                        {images[index]?.name}
                    </div>

                    <div
                        style={{ cursor: "pointer", fontSize: 22 }}
                        onClick={() => {
                            enterFullscreen()
                            setIsAutoPlay(v => !v)
                        }}
                    >
                        {isAutoPlay ? "⏸" : "▶"}
                    </div>
                </div>

                <button
                    onClick={prev}
                    style={{
                        position: "absolute",
                        left: 10,
                        fontSize: 20,
                        color: "white",
                        width: 50,
                        height: 50,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        ...controlStyle,
                        cursor: "pointer",
                        opacity: showUI ? 1 : 0,
                        transition: "0.3s"
                    }}
                >
                    <i className={"fa-solid fa-angle-left"} />
                </button>

                <button
                    onClick={next}
                    style={{
                        position: "absolute",
                        right: 10,
                        fontSize: 20,
                        color: "white",
                        width: 50,
                        height: 50,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        ...controlStyle,
                        cursor: "pointer",
                        opacity: showUI ? 1 : 0,
                        transition: "0.3s"
                    }}
                >
                    <i className={"fa-solid fa-angle-right"} />
                </button>

                <div
                    ref={thumbsRef}
                    onMouseDown={onThumbsMouseDown}
                    onMouseMove={onThumbsMouseMove}
                    onMouseUp={onThumbsMouseUp}
                    onMouseLeave={onThumbsMouseUp}
                    style={{
                        position: "absolute",
                        bottom: "calc(10px + env(safe-area-inset-bottom))",
                        left: "50%",
                        transform: "translateX(-50%)",
                        display: "flex",
                        gap: 6,
                        padding: 6,
                        borderRadius: 10,
                        background: "rgba(0,0,0,0.4)",
                        opacity: showUI ? 1 : 0,
                        transition: "0.3s",
                        maxWidth: "90%",
                        overflowX: "auto",
                        whiteSpace: "nowrap",
                        WebkitOverflowScrolling: "touch",
                        cursor: thumbsDragging.current ? "grabbing" : "grab"
                    }}
                >
                    {images.map((img, i) => (
                        <img
                            referrerPolicy="no-referrer"
                            key={img.id}
                            src={proxyImage(img.thumb)}
                            onClick={() => {
                                userAction()
                                if (thumbsMoved.current) {
                                    thumbsMoved.current = false
                                    return
                                }
                                stopAutoPlay()
                                setLoaded(false)
                                setPrevIndex(index)
                                setDirection(i > index ? 1 : -1)
                                setAnimating(true)
                                setIndex(i)
                            }}
                            alt={img.name || ""}
                            style={{
                                width: 50,
                                height: 50,
                                objectFit: "cover",
                                cursor: "pointer",
                                borderRadius: 4,
                                flex: "0 0 auto",
                                border:
                                    i === index
                                        ? "2px solid white"
                                        : "2px solid transparent"
                            }}
                        />
                    ))}
                </div>
            </div>
        )
    }

    return null
}
